/**
 * Combined Rewarded Ads Hook
 * 
 * This hook provides a sequential dual-ad system that displays:
 * 1. AdsGram rewarded ad first
 * 2. Onclicka rewarded ad second
 * 
 * Rewards are only granted after both ads are completed.
 * Fallback handling ensures graceful degradation if one network fails.
 */

import { useCallback, useState } from 'react';
import { useConfigStore } from '../store/useConfigStore';
import { showRewardedAd, showInterstitialAd } from './useAdsgram';
import { showOnclickaAd } from './useOnclicka';

export type CombinedAdState = 
  | 'idle' 
  | 'loading' 
  | 'showing-first' 
  | 'showing-second' 
  | 'completed' 
  | 'error';

export interface CombinedAdResult {
  success: boolean;
  adsgramCompleted: boolean;
  onclickaCompleted: boolean;
  totalReward: number;
  error?: string;
}

interface UseCombinedAdsOptions {
  /** Called when starting to show ads */
  onStart?: () => void;
  /** Called after both ads complete successfully */
  onComplete?: (result: CombinedAdResult) => void;
  /** Called if the ad sequence fails */
  onError?: (error: string) => void;
  /** Called when transitioning between ads */
  onTransition?: (from: 'adsgram' | 'onclicka', to: 'adsgram' | 'onclicka' | 'complete') => void;
  /** Whether to require both ads to complete (default: false - at least one must complete) */
  requireBoth?: boolean;
  /** Custom ymid for tracking */
  ymid?: string;
}

interface UseCombinedAdsReturn {
  /** Show the combined ad sequence */
  showCombinedAds: () => Promise<CombinedAdResult>;
  /** Show combined ads with interstitial as first ad (for quick view type) */
  showCombinedInterstitialAds: () => Promise<CombinedAdResult>;
  /** Current state of the ad sequence */
  state: CombinedAdState;
  /** Whether ads are currently showing */
  isShowing: boolean;
  /** Get combined reward amount */
  totalReward: number;
}

/**
 * Hook for showing combined rewarded ads (AdsGram first, then Onclicka)
 */
export function useCombinedAds(options: UseCombinedAdsOptions = {}): UseCombinedAdsReturn {
  const { onStart, onComplete, onError, onTransition, requireBoth = false, ymid } = options;
  const [state, setState] = useState<CombinedAdState>('idle');

  // Get config values
  const adsgram = useConfigStore((state) => state.adsgram);
  const onclicka = useConfigStore((state) => state.onclicka);
  const adsEnabled = useConfigStore((state) => state.adsEnabled);

  // Calculate total reward from both networks
  // Fallback values MUST match DEFAULT_CONFIG in backend/src/models/SystemConfig.ts
  const totalReward = (adsgram.rewardAmount ?? 20) + (onclicka.rewardAmount ?? 20);

  const showCombinedAds = useCallback(async (): Promise<CombinedAdResult> => {
    const result: CombinedAdResult = {
      success: false,
      adsgramCompleted: false,
      onclickaCompleted: false,
      totalReward: 0,
    };

    // Check if ads are enabled
    if (!adsEnabled) {
      result.error = 'Ads are disabled';
      onError?.(result.error);
      return result;
    }

    setState('loading');
    onStart?.();

    try {
      // Step 1: Show AdsGram rewarded ad
      setState('showing-first');
      console.log('[CombinedAds] Starting AdsGram rewarded ad...');
      
      let adsgramSuccess = false;
      if (adsgram.enabled) {
        try {
          adsgramSuccess = await showRewardedAd();
          result.adsgramCompleted = adsgramSuccess;
          if (adsgramSuccess) {
            result.totalReward += adsgram.rewardAmount ?? 20;
            console.log('[CombinedAds] AdsGram completed successfully');
          }
        } catch (e) {
          console.warn('[CombinedAds] AdsGram failed:', e);
        }
      } else {
        console.log('[CombinedAds] AdsGram is disabled, skipping...');
      }

      // Notify transition
      onTransition?.('adsgram', 'onclicka');

      // Step 2: Show Onclicka rewarded ad (regardless of AdsGram result for seamless experience)
      setState('showing-second');
      console.log('[CombinedAds] Starting Onclicka rewarded ad...');
      
      let onclickaSuccess = false;
      if (onclicka.enabled) {
        try {
          // Small delay for seamless transition
          await new Promise(resolve => setTimeout(resolve, 300));
          onclickaSuccess = await showOnclickaAd(ymid);
          result.onclickaCompleted = onclickaSuccess;
          if (onclickaSuccess) {
            result.totalReward += onclicka.rewardAmount ?? 100;
            console.log('[CombinedAds] Onclicka completed successfully');
          }
        } catch (e) {
          console.warn('[CombinedAds] Onclicka failed:', e);
        }
      } else {
        console.log('[CombinedAds] Onclicka is disabled, skipping...');
      }

      // Notify transition to complete
      onTransition?.('onclicka', 'complete');

      // Determine success based on requirement mode
      if (requireBoth) {
        // Both ads must complete
        result.success = result.adsgramCompleted && result.onclickaCompleted;
      } else {
        // At least one ad must complete
        result.success = result.adsgramCompleted || result.onclickaCompleted;
      }

      if (result.success) {
        setState('completed');
        onComplete?.(result);
        console.log('[CombinedAds] Sequence completed. Total reward:', result.totalReward);
      } else {
        setState('error');
        result.error = 'Both ad networks failed to display ads';
        onError?.(result.error);
        console.error('[CombinedAds] Sequence failed - no ads completed');
      }

    } catch (error) {
      setState('error');
      result.error = error instanceof Error ? error.message : 'Unknown error occurred';
      onError?.(result.error);
      console.error('[CombinedAds] Sequence error:', error);
    }

    // Reset state after a delay
    setTimeout(() => {
      setState('idle');
    }, 2000);

    return result;
  }, [adsEnabled, adsgram, onclicka, ymid, requireBoth, onStart, onComplete, onError, onTransition]);

  const showCombinedInterstitialAds = useCallback(async (): Promise<CombinedAdResult> => {
    const result: CombinedAdResult = {
      success: false,
      adsgramCompleted: false,
      onclickaCompleted: false,
      totalReward: 0,
    };

    // Check if ads are enabled
    if (!adsEnabled) {
      result.error = 'Ads are disabled';
      onError?.(result.error);
      return result;
    }

    setState('loading');
    onStart?.();

    try {
      // Step 1: Show AdsGram interstitial ad
      setState('showing-first');
      console.log('[CombinedAds] Starting AdsGram interstitial ad...');
      
      let adsgramSuccess = false;
      if (adsgram.enabled) {
        try {
          adsgramSuccess = await showInterstitialAd();
          result.adsgramCompleted = adsgramSuccess;
          if (adsgramSuccess) {
            result.totalReward += adsgram.interstitialReward ?? 15;
            console.log('[CombinedAds] AdsGram interstitial completed successfully');
          }
        } catch (e) {
          console.warn('[CombinedAds] AdsGram interstitial failed:', e);
        }
      } else {
        console.log('[CombinedAds] AdsGram is disabled, skipping...');
      }

      // Notify transition
      onTransition?.('adsgram', 'onclicka');

      // Step 2: Show Onclicka rewarded ad
      setState('showing-second');
      console.log('[CombinedAds] Starting Onclicka rewarded ad...');
      
      let onclickaSuccess = false;
      if (onclicka.enabled) {
        try {
          await new Promise(resolve => setTimeout(resolve, 300));
          onclickaSuccess = await showOnclickaAd(ymid);
          result.onclickaCompleted = onclickaSuccess;
          if (onclickaSuccess) {
            result.totalReward += onclicka.rewardAmount ?? 20;
            console.log('[CombinedAds] Onclicka completed successfully');
          }
        } catch (e) {
          console.warn('[CombinedAds] Onclicka failed:', e);
        }
      } else {
        console.log('[CombinedAds] Onclicka is disabled, skipping...');
      }

      // Notify transition to complete
      onTransition?.('onclicka', 'complete');

      // Determine success
      if (requireBoth) {
        result.success = result.adsgramCompleted && result.onclickaCompleted;
      } else {
        result.success = result.adsgramCompleted || result.onclickaCompleted;
      }

      if (result.success) {
        setState('completed');
        onComplete?.(result);
      } else {
        setState('error');
        result.error = 'Both ad networks failed to display ads';
        onError?.(result.error);
      }

    } catch (error) {
      setState('error');
      result.error = error instanceof Error ? error.message : 'Unknown error occurred';
      onError?.(result.error);
    }

    setTimeout(() => {
      setState('idle');
    }, 2000);

    return result;
  }, [adsEnabled, adsgram, onclicka, ymid, requireBoth, onStart, onComplete, onError, onTransition]);

  return {
    showCombinedAds,
    showCombinedInterstitialAds,
    state,
    isShowing: state === 'showing-first' || state === 'showing-second',
    totalReward,
  };
}

/**
 * Standalone function to show combined rewarded ads
 * Useful when hook pattern is not needed
 */
export async function showCombinedRewardedAds(ymid?: string): Promise<CombinedAdResult> {
  const config = useConfigStore.getState();
  const result: CombinedAdResult = {
    success: false,
    adsgramCompleted: false,
    onclickaCompleted: false,
    totalReward: 0,
  };

  if (!config.adsEnabled) {
    result.error = 'Ads are disabled';
    return result;
  }

  try {
    // Step 1: AdsGram
    if (config.adsgram.enabled) {
      try {
        const success = await showRewardedAd();
        result.adsgramCompleted = success;
        if (success) {
          result.totalReward += config.adsgram.rewardAmount ?? 20;
        }
      } catch (e) {
        console.warn('[showCombinedRewardedAds] AdsGram failed:', e);
      }
    }

    // Step 2: Onclicka (with small delay for transition)
    if (config.onclicka.enabled) {
      await new Promise(resolve => setTimeout(resolve, 300));
      try {
        const success = await showOnclickaAd(ymid);
        result.onclickaCompleted = success;
        if (success) {
          result.totalReward += config.onclicka.rewardAmount ?? 20;
        }
      } catch (e) {
        console.warn('[showCombinedRewardedAds] Onclicka failed:', e);
      }
    }

    result.success = result.adsgramCompleted || result.onclickaCompleted;

  } catch (error) {
    result.error = error instanceof Error ? error.message : 'Unknown error';
  }

  return result;
}

/**
 * Standalone function to show combined interstitial ads
 */
export async function showCombinedInterstitialAds(ymid?: string): Promise<CombinedAdResult> {
  const config = useConfigStore.getState();
  const result: CombinedAdResult = {
    success: false,
    adsgramCompleted: false,
    onclickaCompleted: false,
    totalReward: 0,
  };

  if (!config.adsEnabled) {
    result.error = 'Ads are disabled';
    return result;
  }

  try {
    // Step 1: AdsGram Interstitial
    if (config.adsgram.enabled) {
      try {
        const success = await showInterstitialAd();
        result.adsgramCompleted = success;
        if (success) {
          result.totalReward += config.adsgram.interstitialReward ?? 15;
        }
      } catch (e) {
        console.warn('[showCombinedInterstitialAds] AdsGram failed:', e);
      }
    }

    // Step 2: Onclicka
    if (config.onclicka.enabled) {
      await new Promise(resolve => setTimeout(resolve, 300));
      try {
        const success = await showOnclickaAd(ymid);
        result.onclickaCompleted = success;
        if (success) {
          result.totalReward += config.onclicka.rewardAmount ?? 20;
        }
      } catch (e) {
        console.warn('[showCombinedInterstitialAds] Onclicka failed:', e);
      }
    }

    result.success = result.adsgramCompleted || result.onclickaCompleted;

  } catch (error) {
    result.error = error instanceof Error ? error.message : 'Unknown error';
  }

  return result;
}
